import React, {useCallback, useMemo} from "react";
import {IconButton, Tooltip,} from "@mui/material";
import AgricultureIcon from "@mui/icons-material/Agriculture";
import {Desk, EmployeeRef, INeighbourhood} from "../../api/Model";
import {SeatingPlanModel} from "./SeatingPlanModel";
import {SeatingPlanTab} from "./SeatingPlanTab";
import {usePermission} from "../../util/access/UseAccess";
import {SeatingPlans_Admin} from "../../util/access/Permission";
import {DeskTable} from "../desks/DeskTable";
import {useFormDialogAction} from "../../components/form-dialog/FormDialogProvider";
import {
    ChangeDeskAssignmentDialog,
    ChangeDeskAssignmentForm,
    RevertDeskAssignmentButton
} from "./ChangeDeskAssignmentDialog";
import {emptyEmployee} from "../../api/Empty";
import {DeskModel} from "../desks/DeskModel";
import {Opt} from "../../util/Optional";
import {deskFilterSerializer, DeskTableFilter, filterDesks} from "../desks/DeskTableFilter";
import {useFilter} from "../../util/FilterSerializer";
import {ResidenceModel, useResidenceStore} from "../../api/ResidenceStore";
import {Loading} from "../../components/loading-data/LoadingData";
import {useWorkSiteMapStore, WorkSites} from "../../api/WorkSiteMapStore";
import {RemoteValue} from "../../util/RemoteValue";
import {EmployeeVisitSheet, useEmployeeVisitStore} from "../../api/EmployeeVisitStore";
import {filterOfficeVisitReport} from "../../util/visit/VisitReportSheetUtil";
import {filterDateRange} from "../../util/FilterUtil";
import {SeatingPlanStickyFilter} from "./SeatingPlanStickyFilter";
import {SeatingPlanTabLabel} from "./SeatingPlanTabLabel";
import {DateRangeOption} from "../../components/filter-fields/DateRangeFilter";
import {VisitDataCoverageWarning} from "../employee-visit-report/VisitDataCoverageWarning";


function ActionButton({desk, editAssignment, isDeskModified, canAdmin}: {
    desk: Desk,
    editAssignment: (desk: Desk) => void
    isDeskModified: boolean,
    canAdmin: boolean,
}) {
    const handleClick = useCallback(() => {
        editAssignment(desk);
    }, [desk, editAssignment]);

    return canAdmin ? (
        <Tooltip title={isDeskModified ? 'Desk assignment changed' : 'Re-assign desk'}>
            <IconButton
                size="small"
                color={isDeskModified ? 'warning' : undefined}
                onClick={handleClick}
            >
                <AgricultureIcon fontSize="small"/>
            </IconButton>
        </Tooltip>
    ) : null;
}

export class SeatingPlanDeskModel extends DeskModel {
    constructor(
        private readonly parentModel: SeatingPlanModel,
        readonly canAdmin: boolean,
        residencyModel: ResidenceModel,
        workSites: WorkSites,
        employeeVisits: EmployeeVisitSheet,
        private readonly editAssignment: (desk: Desk) => void,
    ) {
        super(
            parentModel.floors,
            residencyModel,
            parentModel.assignedDesks,
            workSites,
            employeeVisits,
        );
    }

    employee(desk: Desk): Opt<EmployeeRef> {
        return this.parentModel.newDeskAssignment(desk).employee;
    }

    neighbourhood(desk: Desk): Opt<INeighbourhood> {
        return this.parentModel.newDeskAssignment(desk).neighbourhood;
    }

    employeeNeighbourhoods(employee: EmployeeRef): readonly INeighbourhood[] {
        return this.parentModel.modifiedEmployeeNeighbourhood.get(employee);
    }

    neighbourhoodEmployees(neighbourhood: INeighbourhood): readonly EmployeeRef[] {
        const hoodItem = this.parentModel.hoodItems.find(hood => hood.key === neighbourhood.key);
        return hoodItem?.newEmployees ?? [];
    }

    actionButton(desk: Desk): React.ReactNode {
        return (
            <ActionButton
                desk={desk}
                editAssignment={this.editAssignment}
                isDeskModified={this.parentModel.isDeskModified(desk)}
                canAdmin={this.canAdmin}
            />
        );
    }
}

export function useSeatingPlanDeskModel(model: SeatingPlanModel, dateRange: DateRangeOption) {
    const {canAdmin} = usePermission({canAdmin: SeatingPlans_Admin});
    const residencyModel = useResidenceStore().remoteValue;
    const workSites = useWorkSiteMapStore(state => state.remoteValue);
    const employeeVisits = useEmployeeVisitStore(state => state.remoteValue);

    const editAssignment = useFormDialogAction<ChangeDeskAssignmentForm, [Desk]>({
        title: `Change Desk Assignment`,
        description: 'Change the employee and neighbourhood assigned to this desk.',
        submitButtonText: 'Save',
        fullWidth: true,
        extraButtons: RevertDeskAssignmentButton,
        renderForm: ({form, setForm}) => (
            <ChangeDeskAssignmentDialog form={form} setForm={setForm} model={model}/>
        ),
        action: (openDialog, desk) => {
            const newAssignment = model.newDeskAssignment(desk);
            const oldAssignment = model.oldDeskAssignment(desk);
            openDialog({
                initialValue: {
                    desk,
                    employee: newAssignment.employee ? {...emptyEmployee, ...newAssignment.employee} : undefined,
                    neighbourhood: newAssignment.neighbourhood ?? null,
                    previousEmployee: oldAssignment.employee ?? null,
                    previousNeighbourhood: oldAssignment.neighbourhood ?? null,
                },
                onSubmit: form => model.assignDesk(desk, form.neighbourhood, form.employee ?? null),
                successMessage: `Updated assignment for ${desk.name}`,
                errorMessage: `Failed to update assignment for ${desk.name}. Please try again later.`,
            });
        },
    }, [model]);

    return useMemo(() =>
            RemoteValue
                .combine({residencyModel, workSites, employeeVisits})
                .map(({residencyModel, workSites, employeeVisits}) => {
                    return new SeatingPlanDeskModel(
                        model,
                        canAdmin,
                        residencyModel,
                        workSites,
                        filterOfficeVisitReport(employeeVisits, filterDateRange(dateRange)),
                        editAssignment,
                    );
                }),
        [model, canAdmin, editAssignment, residencyModel, workSites, employeeVisits, dateRange]
    );
}

function DesksTab({model}: { model: SeatingPlanModel }) {
    const filterSerializer = useMemo(
        () => deskFilterSerializer(model.hoodItems),
        [model.hoodItems],
    );
    const [filter, setFilter] = useFilter(filterSerializer);

    const deskModel = useSeatingPlanDeskModel(model, filter.dateRange)

    const filtered = useMemo(
        () => deskModel.map(deskModel => {
            const filteredDesks = filterDesks(
                model.desks,
                filter,
                deskModel,
            );
            return {
                deskModel,
                rows: DeskModel.rows(filteredDesks, model.floors),
                length: filteredDesks.length,
            };
        }),
        [model, filter, deskModel],
    );

    return (
        <>
            <SeatingPlanStickyFilter>
                <DeskTableFilter
                    filter={filter}
                    setFilter={setFilter}
                    floors={model.floors}
                    neighbourhoods={model.hoodItems}
                />
            </SeatingPlanStickyFilter>
            <Loading value={filtered}>{({deskModel, rows}) => (
                <>
                    <VisitDataCoverageWarning dates={deskModel.visitDates} dateRange={filter.dateRange}/>
                    <DeskTable model={deskModel} rows={rows}/>
                </>
            )}</Loading>
        </>
    );
}

export const desksTab: SeatingPlanTab<any> = {
    label: (model, active) => (
        <SeatingPlanTabLabel label="Desks" active={active} count={model.plan.deskAssignments.length}/>
    ),
    key: 'desks',
    actions: {},
    content: DesksTab,
};
